import clsx from 'clsx';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { MediaInfoFragment } from 'generated/graphql';

import { ShowroomFilterTree, ShowroomFilterTreeValue } from 'Types/showroom';

import { imageSizes, ImageSizeValues } from 'lib/mediaThresholds';
import { useGetShowroomScrollElement, useIsShowroomMobileVersion, useIsShowroomUserView, useSortShowroomFilterTrees } from 'lib/showroom';

import Slide from '@material-ui/core/Slide';

import FlexLayout from 'components/Common/FlexLayout';
import { RenderFlexElementFn } from 'components/Common/FlexLayout/FlexLayout';
import { HEADER_HEIGHT } from 'components/Showroom/Content/Header/styles';
import { showroomImageLabelHeight } from 'components/Showroom/Content/MediaItem/styles';
import { SHOWROOM_LAYOUT_HEADER_HEIGHT } from 'components/Showroom/Layout/styles';

import ShowroomFilterTreeContainer from '../Filters/FilterTreeContainer';
import ShowroomMobileFilters from './MobileFilters';
import { useStyles } from './styles';

export type RenderItemFn = (item: MediaInfoFragment, sizes: ImageSizeValues) => React.ReactElement;

export type ComponentProps = {
	filters: ShowroomFilterTree[];
	items: MediaInfoFragment[];
	totalItems: number;
	selectedFilters: string[];
	refreshing: boolean;
	renderItem: RenderItemFn;
	onFilterToggle(value: ShowroomFilterTreeValue): void;
	onCloseFilters: () => void;
	onLoadMoreMedias: (page: number) => void;
	showFilters: boolean;
	loading: boolean;
	displayLabel: boolean;
};

const ShowroomContentSectionFiltersLayout = (props: ComponentProps) => {
	const { filters, selectedFilters, items, refreshing, onCloseFilters, onLoadMoreMedias, showFilters, totalItems, loading, displayLabel } = props;
	const { renderItem, onFilterToggle } = props;

	const isUserView = useIsShowroomUserView();
	const isMobileVersion = useIsShowroomMobileVersion();
	const [containerHeight, setContainerHeight] = React.useState(0);
	const containerRef = React.useRef<HTMLDivElement>();
	const scrollElement = useGetShowroomScrollElement();
	const { sorted: sortedFilters } = useSortShowroomFilterTrees(filters);

	const topOffset = isUserView ? HEADER_HEIGHT : HEADER_HEIGHT + SHOWROOM_LAYOUT_HEADER_HEIGHT;
	const classes = useStyles({ fileTreeMaxHeight: containerHeight, topOffset: HEADER_HEIGHT });

	/*
	 * This useEffect calculates the maximum height of the tree based on the available space on the screen
	 * This is calculated as follows: window height - position of the start of the menu
	 * After scrolling past the start of the tree, we only consider the window height and the header offset
	 */
	React.useEffect(() => {
		if (!showFilters) return;
		const handler = () => {
			if (!containerRef.current) return;
			const { y } = containerRef.current.getBoundingClientRect();
			const windowHeight = window.innerHeight;
			const newHeight = Math.min(windowHeight - topOffset, windowHeight - y);
			setContainerHeight(newHeight);
		};
		scrollElement.addEventListener('scroll', handler);
		window.addEventListener('resize', handler);
		handler();
		return () => {
			scrollElement.removeEventListener('scroll', handler);
			window.removeEventListener('resize', handler);
		};
	}, [scrollElement, topOffset, showFilters]);

	const renderFlexItem: RenderFlexElementFn<MediaInfoFragment> = React.useCallback(
		(item, props) => {
			return (
				<div key={props.key} style={props.styleProps}>
					{renderItem(item, imageSizes.default)}
				</div>
			);
		},
		[renderItem]
	);

	const filterTrees = React.useMemo(() => {
		const props = { sortedFilters, selectedFilters, onFilterToggle, containerHeight };
		return isMobileVersion ? (
			<ShowroomMobileFilters {...props} onClose={onCloseFilters} loading={loading} open={showFilters} />
		) : (
			<Slide direction={'right'} in={showFilters} mountOnEnter unmountOnExit>
				<div
					id="filtersContainer"
					className={clsx(classes.showroomFilterTreeContainer, classes.showroomFilterTreeContainerWebkit)}
					ref={containerRef}
				>
					<ShowroomFilterTreeContainer {...props} />
				</div>
			</Slide>
		);
	}, [classes, containerHeight, isMobileVersion, onFilterToggle, selectedFilters, sortedFilters, onCloseFilters, loading, showFilters]);

	const useWindow = scrollElement === window;
	const getScrollParent = React.useCallback(() => scrollElement as any, [scrollElement]);

	return (
		<div className={classes.showroomFiltersContainer}>
			{filterTrees}
			<div className={classes.showroomResultsContainer}>
				<InfiniteScroll
					pageStart={0}
					loadMore={onLoadMoreMedias}
					hasMore={items.length < totalItems}
					useWindow={useWindow}
					getScrollParent={useWindow ? null : getScrollParent}
				>
					<FlexLayout
						elements={items as any}
						displayMode="normal"
						renderElement={renderFlexItem as any}
						maxItemsPerRow={6}
						scrollElement={scrollElement}
						gutterValue={14}
						titleHeight={displayLabel ? showroomImageLabelHeight : 0}
						isMobile={isMobileVersion}
					/>
				</InfiniteScroll>
				<div
					id="filtersRefreshing"
					data-active={refreshing}
					className={clsx(classes.refreshOverlay, { [classes.refreshOverlayActive]: refreshing })}
				></div>
			</div>
		</div>
	);
};

export default React.memo(ShowroomContentSectionFiltersLayout);
